import { cn, Stack } from 'component-library';
import { FC, HTMLAttributes, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { PlotReportFactLabel, PlotReportFactLabelNameEnum, ValueClass } from '@/api/rest/resources/types/fact';
// TODO: MVP-5260 - Bring Confidence Interval back to charts
// import { RenderConfidenceInterval } from './ConfidenceInterval';
// import { useConfidenceIntervalLegendItem } from './hooks/useConfidenceIntervalLegendItem';
import { INDICATOR } from '@/api/rest/resources/types/IndicatorProduct';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { Logger } from '@/lib/logs/logger';
import { useBenchmarkForFact } from '@/pages/shared/hooks/useBenchmarkForFact';
import { isNullish } from '@/utils/booleans';
import { formatUnit } from '@/utils/formatting';
import { printMonthYear } from '@/utils/formatting/date';
import { getColorForIndicator } from '@/utils/getColorForIndicator';

import { BenchmarkPill } from '../Pill/BenchmarkPill';
import { ValueClassPill } from '../Pill/ValueClassPill';
import { Area, Chart, ComposedChart, getXAxisProps, TooltipProps } from './components/Chart';
import { CHART_ZOOM } from './components/ChartZoom';
import { CustomEstimationDot } from './components/CustomEstimationDot';
import { ChartLegendBar } from './components/LegendBar';
import {
  ChartTooltip,
  // ChartTooltipConfidenceInterval,
  ChartTooltipDot,
  ChartTooltipTextPrimary,
  ChartTooltipTextSecondary,
} from './components/Tooltip';
import { ChartLegendBarItem } from './components/types';
import { useEstimatedDataLegendItem } from './hooks/useEstimatedDataLegendItem';
import { useYAxisDomainFromMouseWheelZoom } from './hooks/useYAxisDomainFromMouseWheelZoom';
import { NoChartData } from './NoChartData';
import { extrapolateReferenceLinesChartData, RenderReferenceLine } from './ReferenceLines';
import { ChartProps } from './types';
import {
  getChartDataFromGraphFact,
  getDataDefaultDomain,
  getXAxisDomainForPotentialGraph,
  hasInterpolatedValues,
  intrapolatedValues,
  minMaxAxisDomain,
  sortChartData,
} from './utils';
import { getChartZoomedDomain } from './utils/getChartZoomedDomain';

const waterColors = getColorForIndicator(INDICATOR.WATER);

const chartColors = {
  whc: waterColors,
  confidenceInterval: {
    fill: waterColors.confidenceInterval,
  },
  estimatedData: {
    stroke: waterColors.estimatedData,
  },
} as const;

const totalFactNames = {
  water_holding_capacity_potential: 'whc',
  below_wilting_point_potential: 'wp',
  water_fc_total_benchmark_low: 'whc_benchmark_low',
  water_fc_total_benchmark_moderate: 'whc_benchmark_moderate',
  water_fc_total_benchmark_high: 'whc_benchmark_high',
};

const perHaFactNames = {
  water_holding_capacity_potential_per_ha: 'whc',
  below_wilting_point_potential_per_ha: 'wp',
  water_fc_per_ha_benchmark_low: 'whc_benchmark_low',
  water_fc_per_ha_benchmark_moderate: 'whc_benchmark_moderate',
  water_fc_per_ha_benchmark_high: 'whc_benchmark_high',
};

const zoomMultipliers = {
  min: 0.97,
  max: 1.03,
};

export const WaterHoldingCapacityChart: FC<HTMLAttributes<HTMLDivElement> & ChartProps> = ({
  analysisType,
  data,
  benchmarks,
  className,
  showTooltip,
  zoom,
  setZoom,
  ...delegated
}) => {
  const { t } = useTranslation();

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const defaultDomain = getDataDefaultDomain(data, zoomMultipliers.min, zoomMultipliers.max);
  const yAxisDomainFromMouseWheel = useYAxisDomainFromMouseWheelZoom(chartContainerRef, defaultDomain, setZoom);

  const styles = cn('flex w-full flex-col', className);
  const unit = analysisType === 'total' ? formatUnit(UnitEnum['m^3']) : formatUnit(UnitEnum['m^3/ha']);

  const factNames = analysisType === 'total' ? totalFactNames : perHaFactNames;
  const dateDataMap = getChartDataFromGraphFact(data, factNames);
  const sortedChartData = sortChartData(dateDataMap, (date) => new Date(date).getTime());
  const intrapolatedData = intrapolatedValues(sortedChartData, 'whc');

  const firstTick = sortedChartData.at(0);
  const lastTick = sortedChartData.at(-1);

  if (!firstTick || !lastTick) {
    Logger.error('Not enough data to render chart');
    return (
      <div className={cn(styles, 'h-full justify-center')} {...delegated}>
        <NoChartData />
      </div>
    );
  }

  const xTicksDomain = getXAxisDomainForPotentialGraph(firstTick.label, lastTick.label);
  const xAxisProps = getXAxisProps(xTicksDomain);

  let chartData = extrapolateReferenceLinesChartData(xTicksDomain, intrapolatedData, 'wp');
  chartData = extrapolateReferenceLinesChartData(xTicksDomain, chartData, 'whc_benchmark_low');
  chartData = extrapolateReferenceLinesChartData(xTicksDomain, chartData, 'whc_benchmark_moderate');
  chartData = extrapolateReferenceLinesChartData(xTicksDomain, chartData, 'whc_benchmark_high');

  const hasInterpolated = hasInterpolatedValues(chartData, 'whc');

  const yAxisDomainFromButtons = getChartZoomedDomain({
    data,
    zoom,
    defaultDomain: minMaxAxisDomain(zoomMultipliers.min, zoomMultipliers.max),
  });

  const yAxisDomain = zoom === CHART_ZOOM.MOUSE_WHEEL ? yAxisDomainFromMouseWheel : yAxisDomainFromButtons;

  return (
    <>
      <Chart.Container
        ref={chartContainerRef}
        {...delegated}
        data={chartData}
        config={{
          whc: {
            // eslint-disable-next-line sonarjs/no-duplicate-string
            label: t('global.analysis.waterHoldingCapacity'),
            color: chartColors.whc.stroke,
          },
        }}
      >
        <ComposedChart data={chartData}>
          <defs>
            <linearGradient id='whc' x1='0' y1='-0.86' x2='0' y2='1'>
              <stop offset='0%' stopColor={chartColors.whc.fill} stopOpacity={1} />
              <stop offset='100%' stopColor={chartColors.whc.fill} stopOpacity={0} />
            </linearGradient>
          </defs>
          {/* TODO: MVP-5260 - Bring Confidence Interval back to charts  */}
          {/* {RenderConfidenceInterval({
            dataKey: 'whc.confidence_interval',
            type: 'monotone',
            fill: chartColors.confidenceInterval.fill,
            id: 'whc-confidence-interval',
          })} */}
          <Area
            type='monotone'
            stroke='var(--color-whc)'
            strokeWidth={2}
            fill='url(#whc)'
            fillOpacity={1}
            dataKey='whc.value'
            connectNulls
            activeDot={ChartTooltipDot}
            dot={(props) => <CustomEstimationDot {...props} color={chartColors.estimatedData.stroke} />}
            id='whc-area'
          />
          {RenderReferenceLine({
            dataKey: 'wp.value',
            type: 'stepBefore',
            label: chartData.some((point) => !!point.wp) ? t('global.analysis.wiltingPoint') : null,
            chartData,
            id: 'wp-line',
          })}
          {RenderReferenceLine({
            dataKey: 'whc_benchmark_low.value',
            type: 'monotone',
            label: chartData.some((point) => !!point.whc_benchmark_low) ? t('global.benchmark.low') : null,
            chartData,
            id: 'whc-benchmark-low-line',
            yAxisDomain: yAxisDomain as [number, number],
          })}
          {RenderReferenceLine({
            dataKey: 'whc_benchmark_moderate.value',
            type: 'monotone',
            label: chartData.some((point) => !!point.whc_benchmark_moderate) ? t('global.benchmark.moderate') : null,
            chartData,
            id: 'whc-benchmark-moderate-line',
            yAxisDomain: yAxisDomain as [number, number],
          })}
          {RenderReferenceLine({
            dataKey: 'whc_benchmark_high.value',
            type: 'monotone',
            label: chartData.some((point) => !!point.whc_benchmark_high) ? t('global.benchmark.high') : null,
            chartData,
            id: 'whc-benchmark-high-line',
            yAxisDomain: yAxisDomain as [number, number],
          })}
          <Chart.XAxis {...xAxisProps} className='whc-x-axis' />
          <Chart.YAxis domain={yAxisDomain} className='whc-y-axis' allowDataOverflow={true}>
            <Chart.Label>{unit}</Chart.Label>
          </Chart.YAxis>
          {showTooltip && (
            <Chart.Tooltip
              content={(props) => <CustomTooltip {...props} unit={unit as UnitEnum} benchmarks={benchmarks} />}
            />
          )}
        </ComposedChart>
      </Chart.Container>
      <CustomLegendBar showEstimate={hasInterpolated} />
    </>
  );
};

const CustomLegendBar = ({ showEstimate }: { showEstimate: boolean }) => {
  const { t } = useTranslation();
  // const confidenceIntervalLegendItem = useConfidenceIntervalLegendItem({
  //   confidenceIntervalColor: chartColors.confidenceInterval.fill,
  // });

  const estimatedDataLegendItem = useEstimatedDataLegendItem({
    estimatedDataColor: chartColors.estimatedData.stroke ?? '',
  });

  const legendBarItems: ChartLegendBarItem[] = [
    {
      label: t('global.analysis.waterHoldingCapacity'),
      color: chartColors.whc.fill,
    },
    ...(showEstimate ? estimatedDataLegendItem : []),
    // ...confidenceIntervalLegendItem,
  ];

  return <ChartLegendBar items={legendBarItems} data-testid='water-holding-capacity-chart-legend' />;
};

const CustomTooltip = ({
  active,
  payload,
  label,
  unit,
  benchmarks,
}: // eslint-disable-next-line @typescript-eslint/no-explicit-any
TooltipProps<ValueType, NameType> & { unit: UnitEnum; benchmarks?: any[] }) => {
  const { t } = useTranslation();

  const whcValue = payload?.find((data) => data.dataKey === 'whc.value');
  const whcDisplay = !isNullish(whcValue?.value)
    ? `${getDisplayNumber(whcValue?.value as string, window.navigator.language)} ${unit}`
    : t('global.analysis.noData');

  const whcBenchmarkType = useBenchmarkForFact(benchmarks, whcValue?.value);
  const dataPointHasLabel = whcValue && whcValue?.payload?.whc?.labels?.length > 0;
  const dataPointValueClass = dataPointHasLabel
    ? whcValue.payload.whc?.labels?.find(
        (dataLabel: PlotReportFactLabel) => dataLabel.name === PlotReportFactLabelNameEnum.value_class,
      )
    : undefined;

  const dataLabel = dataPointValueClass?.value as ValueClass;

  if (!active) return null;

  return (
    <ChartTooltip>
      <Stack spacing={8} direction='row' className='justify-between'>
        <ChartTooltipTextSecondary>{printMonthYear(label)}</ChartTooltipTextSecondary>
        {dataLabel && <ValueClassPill title={dataLabel} />}
      </Stack>
      {whcBenchmarkType && (
        <ChartTooltipTextSecondary>
          <BenchmarkPill benchmarkType={whcBenchmarkType} />
        </ChartTooltipTextSecondary>
      )}
      <Stack spacing={8} direction='row' className='justify-between'>
        <ChartTooltipTextPrimary>{`${t('global.analysis.waterHoldingCapacity')}`}</ChartTooltipTextPrimary>
        <ChartTooltipTextPrimary>{whcDisplay}</ChartTooltipTextPrimary>
      </Stack>
      {/* TODO: MVP-5260 - Bring Confidence Interval back to charts  */}
      {/* <ChartTooltipConfidenceInterval ciPct={whcValue?.payload?.whc?.confidence_interval_pct} /> */}
    </ChartTooltip>
  );
};

export { CustomTooltip as CustomTooltipWaterHoldingCapacity };
